import { useState } from "react";
import { ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { PROJECT_TYPES } from "../../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
    "w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 transition-[border-color,box-shadow] duration-300 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/15";

export default function ContactForm() {
    const [form, setForm] = useState({ name: "", email: "", company: "", project_type: PROJECT_TYPES[0], message: "" });
    const [sending, setSending] = useState(false);

    const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setSending(true);
        try {
            await axios.post(`${API}/enquiries`, {
                name: form.name.trim(),
                email: form.email.trim(),
                company: form.company.trim() || null,
                project_type: form.project_type,
                message: form.message.trim(),
            });
            toast.success("Enquiry sent", {
                description: "Thanks — we'll get back to you with a clear technical plan.",
            });
            setForm({ name: "", email: "", company: "", project_type: PROJECT_TYPES[0], message: "" });
        } catch (err) {
            toast.error("Could not send enquiry", {
                description: "Please check your details and try again.",
            });
        } finally {
            setSending(false);
        }
    };

    return (
        <form onSubmit={submit} data-testid="enquiry-form" className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                    <label htmlFor="enquiry-name" className="mb-1.5 block font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                        Name *
                    </label>
                    <input id="enquiry-name" data-testid="contact-name-input" required minLength={2} value={form.name} onChange={set("name")} placeholder="Your name" className={inputCls} />
                </div>
                <div>
                    <label htmlFor="enquiry-email" className="mb-1.5 block font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                        Email *
                    </label>
                    <input id="enquiry-email" data-testid="contact-email-input" required type="email" value={form.email} onChange={set("email")} placeholder="you@company.com" className={inputCls} />
                </div>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                    <label htmlFor="enquiry-company" className="mb-1.5 block font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                        Company
                    </label>
                    <input id="enquiry-company" data-testid="contact-company-input" value={form.company} onChange={set("company")} placeholder="Optional" className={inputCls} />
                </div>
                <div>
                    <label htmlFor="enquiry-type" className="mb-1.5 block font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                        Project type *
                    </label>
                    <select id="enquiry-type" data-testid="contact-type-select" value={form.project_type} onChange={set("project_type")} className={inputCls}>
                        {PROJECT_TYPES.map((t) => (
                            <option key={t} value={t}>
                                {t}
                            </option>
                        ))}
                    </select>
                </div>
            </div>
            <div>
                <label htmlFor="enquiry-message" className="mb-1.5 block font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                    What are you building? *
                </label>
                <textarea
                    id="enquiry-message"
                    data-testid="contact-message-input"
                    required
                    minLength={10}
                    rows={5}
                    value={form.message}
                    onChange={set("message")}
                    placeholder="Goals, timeline, anything useful…"
                    className={`${inputCls} resize-none`}
                />
            </div>
            <button
                type="submit"
                data-testid="enquiry-form-submit-button"
                disabled={sending}
                className="group flex w-full items-center justify-center gap-2.5 rounded-full bg-brand px-7 py-4 text-sm font-semibold text-white shadow-card transition-[background-color,transform,box-shadow,opacity] duration-300 hover:bg-brand-dark hover:shadow-hover active:scale-[0.98] disabled:opacity-60"
            >
                {sending ? "Sending…" : "Send Enquiry"}
                {!sending && <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />}
            </button>
        </form>
    );
}
